import { Key, useMemo, useState } from "react";
import * as R from "ramda";
import { useAppSelector, useAppTranslation } from "@/utils";
import { Flex, Menu, Typography, usePaypingContext } from "@payping/web-components";
import { FeatherLeft, PhRFile, PhRGridFour, PhRMagicWand, PhRPercent, PhRUsers } from "@payping/icons";
import { Link, useLocation } from "react-router-dom";
import { DashboardRoutes } from "@/router";
import { ActivationSteps } from "@payping/api/app/api/bnplMerchantTypes";
import type { MenuProps } from "@payping/web-components/components/menu";
import { useSelectedTab } from "./useSelectedTab";

interface MenuItem {
  key: string;
  children?: MenuItem[];
}

const flattenKeys: (menuItems: MenuItem[]) => string[] = R.pipe(
  R.map((item: MenuItem) =>
    item.children && item.children.length > 0 ? [item.key, ...flattenKeys(item.children)] : [item.key],
  ),
  R.flatten,
);

const ShopMenu = () => {
  const { t } = useAppTranslation();
  const { breakpoints } = usePaypingContext();
  const { lg } = breakpoints;
  const contract = useAppSelector((state) => state.bnplMerchant.contract);
  const selectedTab = useSelectedTab();

  const bnplNeedToActive = !(
    contract?.next === ActivationSteps.NoNext ||
    contract?.next === ActivationSteps.WaitingForAdmin ||
    contract?.next === ActivationSteps.SignContract
  );

  const location = useLocation();

  const menuItems = useMemo(() => {
    const baseMeun: { icon: JSX.Element; label: JSX.Element | string; key: string; children?: any[] }[] = [
      {
        key: DashboardRoutes.myShop.product.list.url,
        icon: <PhRGridFour />,
        label: <Link to={DashboardRoutes.myShop.product.list.url}>{t("shop.tabs.product")}</Link>,
      },
      {
        key: DashboardRoutes.myShop.invoice.list.url,
        icon: <PhRFile />,
        label: <Link to={DashboardRoutes.myShop.invoice.list.url}>{t("shop.tabs.invoice")}</Link>,
      },
      {
        key: DashboardRoutes.myShop.customer.list.url,
        icon: <PhRUsers />,
        label: <Link to={DashboardRoutes.myShop.customer.list.url}>{t("shop.tabs.customer")}</Link>,
      },
      {
        key: DashboardRoutes.myShop.coupon.list.url,
        icon: <PhRPercent />,
        label: <Link to={DashboardRoutes.myShop.coupon.list.url}>{t("shop.tabs.coupon")}</Link>,
      },
      {
        key: DashboardRoutes.myShop.bnpl.root.url,
        icon: <PhRMagicWand />,
        label: bnplNeedToActive ? (
          <Link to={DashboardRoutes.myShop.bnpl.activation.root.url}>{t("shop.tabs.bnpl.root")}</Link>
        ) : (
          t("shop.tabs.bnpl.root").toString()
        ),
        children: bnplNeedToActive
          ? undefined
          : [
              {
                key: DashboardRoutes.myShop.bnpl.list.root.url,
                label: <Link to={DashboardRoutes.myShop.bnpl.list.root.url}>{t("shop.tabs.bnpl.list")}</Link>,
              },
              {
                key: DashboardRoutes.myShop.bnpl.manage.root.url,
                label: <Link to={DashboardRoutes.myShop.bnpl.manage.root.url}>{t("shop.tabs.bnpl.manage")}</Link>,
              },
              {
                key: DashboardRoutes.myShop.bnpl.financialCalender.root.url,
                label: (
                  <Link to={DashboardRoutes.myShop.bnpl.financialCalender.root.url}>
                    {t("common.financialCalender")}
                  </Link>
                ),
              },
              {
                key: DashboardRoutes.myShop.bnpl.plans.root.url,
                label: <Link to={DashboardRoutes.myShop.bnpl.plans.root.url}>{t("shop.tabs.bnpl.plans")}</Link>,
              },
            ],
      },
    ];

    return baseMeun;
  }, [t, bnplNeedToActive]);

  const flatedKeys = flattenKeys(menuItems);

  const [openKeys, setOpenKeys] = useState<string[]>(() => {
    const temp: string[] = [];
    flatedKeys.forEach((i) => {
      if (location.pathname.includes(i)) {
        temp.push(i);
      }
    });
    return temp;
  });

  const handleClick: MenuProps["onClick"] = (e) => {
    const isTopLevel = !menuItems.find((item) => (item?.children || []).some((child) => child.key === e.key));

    if (isTopLevel) {
      setOpenKeys([]);
    }
  };

  const handleOpenChange = (keys: Key[]) => {
    setOpenKeys(keys.map((i) => i.toString()));
  };

  const desktopMenu = (
    <Flex vertical className={"tw:h-full!"}>
      {/* < تایتل "راهکارها" > */}
      <Typography.Text type={"secondary"}>{t("mainNav.tabs.solutions")}</Typography.Text>

      {/* <منو مایشاپ > */}
      <Menu
        mode="inline"
        className={"tw:h-full! tw:border-b-0!"}
        items={menuItems}
        onClick={handleClick}
        selectedKeys={[selectedTab]}
        onOpenChange={handleOpenChange}
        openKeys={openKeys}
      />
    </Flex>
  );

  const mobileMenu = (
    <Flex vertical className={"tw:p-4! tw:py-12!"}>
      {/* < تایتل "راهکارها" > */}
      <Typography.Title className={"tw:mb-8! tw:font-medium"} level={4}>
        {t("mainNav.tabs.solutions")}
      </Typography.Title>

      {/* <منو مایشاپ > */}
      <Menu
        mode="inline"
        className="tw:border-0"
        items={menuItems.map((item) => {
          if (!item.children) {
            return {
              ...item,
              label: (
                <div className={"tw:flex tw:justify-between"}>
                  <Flex justify={"space-between"}>{item.label}</Flex>
                  <FeatherLeft width={14} />
                </div>
              ),
            };
          }
          return item;
        })}
        onOpenChange={handleOpenChange}
        openKeys={openKeys}
      />
    </Flex>
  );

  return lg ? desktopMenu : mobileMenu;
};

export default ShopMenu;
